import axios from 'axios';
import { authFetch } from './authFetch.js';

/**
 * CampgroundService — queries campgrounds near a route
 * 
 * Returns campgrounds within 15 miles of the route corridor.
 * All users see all campgrounds. Pro unlocks filtering.
 */
export class CampgroundService {
    constructor() {
        this.backendUrl = import.meta.env.VITE_BACKEND_URL || '';
        this.useBackend = import.meta.env.VITE_USE_BACKEND === 'true';
    }

    /**
     * Get campgrounds near a route.
     * @param {Array} waypoints - Array of {lat, lng} objects along the route
     * @param {string} plan - User plan
     * @returns {Object} { campgrounds: [...], count }
     */
    async getCampgroundsNearRoute(waypoints, plan = 'free') {
        if (!this.useBackend || !this.backendUrl) {
            console.log('⚠️ Campgrounds: backend not configured');
            return { campgrounds: [], count: 0 };
        }

        if (!waypoints || waypoints.length < 2) {
            return { campgrounds: [], count: 0 };
        }

        // Sample waypoints to reduce payload — every 10th point, max 20
        const sampled = this.sampleWaypoints(waypoints, 20);

        try {
            const response = await axios.post(`${this.backendUrl}/api/campgrounds/near-route`, {
                waypoints: sampled,
                plan
            }, { timeout: 30000 });

            if (response.data && response.data.status === 'success') {
                console.log(`🏕️ Campgrounds: ${response.data.count} found near route`);
                return response.data;
            }

            return { campgrounds: [], count: 0 };
        } catch (error) {
            console.warn('Campgrounds fetch failed:', error.message);
            return { campgrounds: [], count: 0 };
        }
    }

    /**
     * Submit a new campground (community contribution)
     * @param {Object} campground - { name, lat, lng, type, amenities, notes }
     */
    async submitCampground(campground) {
        if (!this.useBackend || !this.backendUrl) {
            return { status: 'error', message: 'Backend not configured' };
        }

        try {
            const response = await authFetch(`${this.backendUrl}/api/campgrounds`, {
                method: 'POST',
                body: JSON.stringify(campground)
            });
            return await response.json();
        } catch (error) {
            console.error('Campground submission failed:', error.message);
            return { status: 'error', message: 'Failed to submit campground' };
        }
    }

    /**
     * Verify an existing campground (community verification)
     * @param {string} campgroundId
     */
    async verifyCampground(campgroundId) {
        if (!this.useBackend || !this.backendUrl) {
            return { status: 'error', message: 'Backend not configured' };
        }

        try {
            const response = await authFetch(`${this.backendUrl}/api/campgrounds/${campgroundId}/verify`, {
                method: 'POST',
                body: JSON.stringify({ campgroundId })
            });
            return await response.json();
        } catch (error) {
            console.error('Campground verification failed:', error.message);
            return { status: 'error', message: 'Failed to verify campground' };
        }
    }

    /**
     * Sample waypoints evenly to reduce API payload.
     */
    sampleWaypoints(waypoints, maxPoints) {
        if (waypoints.length <= maxPoints) return waypoints;

        const sampled = [];
        const step = (waypoints.length - 1) / (maxPoints - 1);

        for (let i = 0; i < maxPoints; i++) {
            const index = Math.round(i * step);
            const wp = waypoints[index];
            sampled.push({ lat: wp.lat !== undefined ? wp.lat : wp[0], lng: wp.lng !== undefined ? wp.lng : wp[1] });
        }

        return sampled;
    }
}
